import React, { useContext, useEffect, useState } from 'react';
import { FormContext } from '../../FormContext';
import { getDateDepend, getMinMaxDate } from '../../utils/utilFunctions';
import InputAddRemove from './InputAddRemove';
import { isFieldSigned, determineIsDisabled } from './utils.js';
import moment from 'moment';

const Input = ({ field_multiple_primary, field_multiple_id, field_multiple, field_autocomplete, field_type, field_disabled_approver, field_column, field_id, field_label, field_placeholder, field_disabled, field_mandatory, field_value = "", field_date_depend, field_number_max, field_number_min, field_multiple_type, field_date_min, field_date_max, field_price }) => {
  const { handleChangeInput, formElements, formStatus, formPageType, formApprovalIndex, formUserLock, formMultipleType, formChatPerson, formOfficers } = useContext(FormContext);
  const [minDate, setMinDate] = useState(field_date_min || null);
  const [maxDate, setMaxDate] = useState(field_date_max || null);
  const [inputValue, setInputValue] = useState(field_value);

  // Prevent scroll from changing number input value
  const handleWheel = (e) => {
    if (document.activeElement === e.target) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    const inputElement = document.getElementById(field_id);
    if (inputElement && field_type === 'number') {
      inputElement.addEventListener('wheel', handleWheel, { passive: false });
      return () => {
        inputElement.removeEventListener('wheel', handleWheel);
      };
    }
  }, [field_id, field_type]);

  // Summation Logic
  useEffect(() => {
    if (field_id?.startsWith('summation_')) {
      let sum = 0;
      formElements.pages?.forEach(page => {
        page.parts?.forEach(part => {
          part.fields?.forEach(field => {
            if (!field.skip_summation) {
              if (field.field_price !== undefined && 
                  !field.field_id?.startsWith('summation_') && 
                  field.field_value !== undefined && 
                  field.field_value !== '') {
                const price = parseFloat(field.field_price) || 0;
                sum += price;
              }
              if (field.field_options && field.field_value) {
                const selectedOption = field.field_options.find(opt => 
                  opt.option_label === field.field_value
                );
                if (selectedOption?.option_price !== undefined && !selectedOption.skip_summation) {
                  const optionPrice = parseFloat(selectedOption.option_price) || 0;
                  sum += optionPrice;
                }
              }
            }
          });
        });
      });
      
      if (sum.toString() !== inputValue) {
        setInputValue(sum.toString());
        handleChangeInput(field_id, { target: { value: sum.toString() } });
      }
    }
  }, [formElements]);

  // --- MODIFIED: Handle Input Change (Removed Aggressive Validation) ---
  const handleInputChange = (event) => {
    let newValue = event.target.value;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(newValue)) {
      newValue = newValue.toLowerCase();
      event.target.value = newValue;
    }
    
    // REMOVED: The block that checked field_number_min/max here. 
    // We allow the user to type freely.

    if (field_type === 'date') {
      const date = moment(newValue);
      if (!newValue || ((!minDate || date.isSameOrAfter(minDate)) && (!maxDate || date.isSameOrBefore(maxDate)))) {
        setInputValue(newValue);
        handleChangeInput(field_id, event);
      }
    } else if (field_type === 'number' && field_price !== undefined) {
      setInputValue(newValue);
      // Update both value and price
      handleChangeInput(field_id, {
        ...event,
        target: { 
          ...event.target, 
          value: newValue,
          price: newValue 
        }
      });

      // Recalculate Summation immediately for price fields
      const summationFields = [];
      formElements.pages?.forEach(page => {
        page.parts?.forEach(part => {
          part.fields?.forEach(field => {
            if (field.field_id?.startsWith('summation_')) {
              summationFields.push(field.field_id);
            }
          });
        });
      });

      let sum = 0;
      formElements.pages?.forEach(page => {
        page.parts?.forEach(part => {
          part.fields?.forEach(field => {
            if (field.field_price !== undefined && !field.field_id?.startsWith('summation_')) {
              const price = field.field_id === field_id ? 
                parseFloat(newValue) || 0 : 
                parseFloat(field.field_price) || 0;
              sum += price;
            }
          });
        });
      });

      summationFields.forEach(summationId => {
        handleChangeInput(summationId, { target: { value: sum.toString() } });
      });
    } else {
      setInputValue(newValue);
      handleChangeInput(field_id, event);
    }
  };

  // --- NEW: Handle Blur (Validate Min/Max ONLY when user leaves field) ---
  const handleBlur = (event) => {
    // 1. Run existing hideTimePicker logic
    if (field_type === 'date' || field_type === 'time') {
      window['hideTimePicker'](field_id);
    }

    // 2. Run Min/Max Validation for Numbers
    if (field_type === 'number' && inputValue !== "") {
      let correctedValue = inputValue;
      const numVal = Number(inputValue);

      if (field_number_min !== undefined && numVal < field_number_min) {
        correctedValue = field_number_min.toString();
      } else if (field_number_max !== undefined && numVal > field_number_max) {
        correctedValue = field_number_max.toString();
      }

      // If value changed due to validation, update state and trigger change
      if (correctedValue !== inputValue) {
        setInputValue(correctedValue);
        
        // Create a synthetic event to update the parent form state
        const syntheticEvent = {
            target: {
                value: correctedValue,
                id: field_id,
                ...(field_price !== undefined ? { price: correctedValue } : {})
            }
        };
        handleChangeInput(field_id, syntheticEvent);
      }
    }
    
    // 3. Ensure parent knows about the blur
    handleChangeInput(field_id, event);
  };

  useEffect(() => {
    if (typeof field_date_depend !== 'undefined') {
      const date_depend_arr = field_date_depend.split("/");
      try {
        const date_depend = getDateDepend(formElements, date_depend_arr[0]);
        const minmax_date = getMinMaxDate(date_depend, date_depend_arr[1], date_depend_arr[2], date_depend_arr[3]);
        setMinDate(minmax_date.min_date);
        setMaxDate(moment(minmax_date.max_date).format("YYYY-MM-DD"));
      } catch (error) {
        console.log(error)
      }
    } 
    if(field_date_min && field_date_max) {
      setMinDate(field_date_min);
      setMaxDate(field_date_max);
    }
  }, [formElements, field_date_depend, field_date_min, field_date_max]);

  useEffect(() => {
    if (!field_id?.startsWith('summation_')) {
      setInputValue(field_value);
    }
  }, [field_value, field_id]);

  let isSigned = isFieldSigned(field_id, formMultipleType, formChatPerson, formOfficers, field_multiple_type);

  const handleAutoComplete = (event, field_autocomplete) => {
    if (field_autocomplete && field_autocomplete === "User" && event.target.value.length > 2) {
      window[`autocomplete${field_autocomplete}`](event.target.value, field_id);
    }
  };

  const handleFocus = (field_id, event, field_type) => {
    switch (field_type) {
      case 'date':
        window['triggerDatePicker'](field_id, minDate, maxDate);
        break;
      case 'time':
        window['triggerTimePicker'](field_id);
        break;
      default:
        break;
    }
    handleChangeInput(field_id, event);
  };

  // --- MODIFIED: Removed Number Validation from here ---
  // We only validate dates here for display purposes. 
  // We do NOT want to lock the display value for numbers while typing.
  const validateMinMaxDisplay = (value) => {
    if (!value) return value;

    if (field_type === 'date') {
      const dateValue = moment(value);
      if (minDate && dateValue.isBefore(minDate)) {
        return moment(minDate).format('YYYY-MM-DD');
      }
      if (maxDate && dateValue.isAfter(maxDate)) {
        return moment(maxDate).format('YYYY-MM-DD');
      }
      return value;
    }
    return value;
  };

  const isDisabled = determineIsDisabled(
    formUserLock,
    formPageType,
    field_multiple_id,
    isFieldSigned(field_id, formMultipleType, formChatPerson, formOfficers, field_multiple_type),
    field_id,
    formApprovalIndex,
    field_disabled_approver,
    field_disabled,
    formStatus
  ) || field_id?.startsWith('summation_');

  return (
    <div className={`form-group ${field_column ? field_column : 'col-sm-6'}`}>
      <label>
        <span dangerouslySetInnerHTML={{ __html: field_label }}></span>
        <font color="red">{field_mandatory === 'yes' ? '*' : ''}</font>
      </label>
      <input
        type={field_type === 'number' ? 'number' : 'text'}
        placeholder={field_placeholder ? field_placeholder : ' '}
        value={validateMinMaxDisplay(inputValue)}
        disabled={isDisabled}
        onChange={handleInputChange}
        onFocus={event => handleFocus(field_id, event, field_type)}
        onKeyUp={(event) => handleAutoComplete(event, field_autocomplete)}
        min={minDate || field_number_min}
        max={maxDate || field_number_max}
        className="form-control form-input force-info"
        id={field_id}
        autoComplete="off"
        data-target-input="nearest"
        data-target={`#${field_id}`}
        data-toggle={field_type === 'time' && "datetimepicker"}
        onBlur={handleBlur} 
      />
      {!formUserLock && <InputAddRemove field_id={field_id} field_multiple_id={field_multiple_id} field_multiple_type={field_multiple_type} />}
    </div>
  );
};

export default Input;